/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { CategoryHeader } from './category-header';
import { DesktopFilters, MobileFilters } from './filters';
import { Pagination } from '@theme/components';
import { ProductItem } from '@theme/views/product-item';
import { GetCategoryResponse, WidgetResultType } from '@akinon/next/types';
import { useAppDispatch, useAppSelector } from '@akinon/next/redux/hooks';
import { setFacets, setMenuOpen } from '@theme/redux/reducers/category';
import CategoryActiveFilters from '@theme/views/category/category-active-filters';
import { useLocalization, useMediaQuery } from '@akinon/next/hooks';
import { Link, LoaderSpinner } from '@akinon/next/components';
import { ROUTES } from '@theme/routes';
import { useRouter } from '@akinon/next/hooks';
import { RootState } from '@theme/redux/store';
import { CardBadgesType, WIDGET_TYPE } from '@theme/types';
import {
  pushFilterInteraction,
  pushProductListProductViewed
} from '@theme/utils/gtm';
import {
  findFilterLabels,
  getActiveFilterLabels,
  useTrackableFacets
} from './facet-grouping';
import { pcCategoryView } from '@theme/utils/personaclick';
import { useGetWidgetQuery } from '@akinon/next/data/client/misc';
import PcRecommendationClient from '@theme/widgets/pc-recommendations/client';
import {
  applyCategoryFilterSettings,
  syncCategorySelection,
  useCategoryFilterSettings
} from './filters/category-filter-settings';
import { ShoppableListingBannerType } from '@theme/widgets/shoppable-listing-banner/type';
import ListingBanner, {
  getListingBannerSlots
} from '@theme/views/shoppable-banner/listing-banner';

interface ListPageProps {
  data: GetCategoryResponse;
  cardBadges?: WidgetResultType<CardBadgesType>;
  useDefaultDimension?: boolean;
  pinListingBanner?: ShoppableListingBannerType;
}

export default function ListPage(props: ListPageProps) {
  const { data, useDefaultDimension } = props;

  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const matches = useMediaQuery('(max-width: 768px)');
  const [matchesStatus, setMatchesStatus] = useState(false);
  const [tempSearchParams, setTempSearchParams] = useState(
    new URLSearchParams(searchParams)
  );
  const { isMenuOpen, selectedFacets } = useAppSelector(
    (state: RootState) => state.category
  );
  const trackableFacets = useTrackableFacets();

  const { data: recommendation, isSuccess: recoSuccess } = useGetWidgetQuery(
    'pc-category-recommendation'
  );

  const { data: emptyRecommendation, isSuccess: emptyRecoSuccess } =
    useGetWidgetQuery('pc-search-empty-results');

  const searchText = searchParams.get('search_text');

  const { products = [], facets = [] } = data;

  const currentPageUrl =
    data?.special_page?.url || data?.category?.absolute_url || '';

  const categoryFilterSettings = useCategoryFilterSettings(currentPageUrl);

  const page = useMemo(
    () => Number(searchParams?.get('page') ?? 1),
    [searchParams]
  );

  const listingBannerSlots = useMemo(
    () => (page === 1 ? getListingBannerSlots(props.pinListingBanner) : []),
    [page, props.pinListingBanner]
  );

  const renderListingBanners = (
    isMatch: (productIndex: number) => boolean
  ) =>
    listingBannerSlots
      .filter((slot) => isMatch(slot.productIndex))
      .map((slot) => (
        <ListingBanner
          key={slot.key}
          image={slot.image}
          alt={slot.alt}
          url={slot.url}
          pins={slot.pins}
          isMobile={slot.isMobile}
        />
      ));

  useEffect(() => {
    setMatchesStatus(matches);
  }, [matches]);

  useEffect(() => {
    if (page > 1 && products.length === 0) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('page');
      router.push(newUrl.pathname + newUrl.search, undefined);
    }
  }, [products, page, router]);

  const appliedQuery = searchParams.toString();

  /**
   * `tempSearchParams` yalnızca mount'ta URL'den kuruluyor. Sıralama, sayfalama
   * ve geri/ileri gibi panel dışı navigasyonlar URL'i değiştirdiğinde panel
   * state'i geride kalıyor; sonraki filtre tıklaması hem o eski seçimleri URL'e
   * geri yazıyor hem de `filter_interaction` event'ini eski listeyle basıyordu.
   * Uygulanan query değiştikçe tazelenir.
   *
   * Mobil filter açıkken atlanır: orada seçimler "Uygula"ya kadar bekletiliyor,
   * araya giren bir senkron bekleyen seçimleri silerdi. Query zaten eşitse
   * referans korunur, aksi hâlde DesktopFilters'ın push efekti boşuna tetiklenir.
   */
  useEffect(() => {
    if (matchesStatus && isMenuOpen) {
      return;
    }

    setTempSearchParams((current) =>
      current.toString() === appliedQuery
        ? current
        : new URLSearchParams(appliedQuery)
    );
  }, [appliedQuery, matchesStatus, isMenuOpen]);

  useEffect(() => {
    const appliedParams = new URLSearchParams(appliedQuery);

    dispatch(
      setFacets(
        applyCategoryFilterSettings(
          syncCategorySelection(facets, appliedParams),
          categoryFilterSettings
        )
      )
    );
  }, [facets, dispatch, categoryFilterSettings, appliedQuery]);

  useEffect(() => {
    localStorage.setItem(
      'ga4ListInfo',
      JSON.stringify({
        item_list_id:
          data?.category?.pk.toString() || window?.location?.pathname,
        item_list_name: data?.category?.name || window?.location?.pathname
      })
    );
  }, [data]);

  useEffect(() => {
    if (data?.products?.length > 0) {
      pushProductListProductViewed(
        data?.products,
        data?.pagination?.current_page,
        data?.pagination?.page_size
      );

      if (data?.category?.pk || data?.special_page?.pk) {
        pcCategoryView(data?.category?.pk || data?.special_page?.pk);
      }
    }
  }, [data?.products]);

  const handleRemoveSelectedFilter = (facet, choice) => {
    const newParams = new URLSearchParams(tempSearchParams.toString());

    if (facet.widget_type === WIDGET_TYPE.category) {
      newParams.delete(facet.search_key);
    } else {
      const remaining = newParams
        .getAll(facet.search_key)
        .filter((val) => String(val) !== String(choice.value));
      newParams.delete(facet.search_key);
      remaining.forEach((val) => newParams.append(facet.search_key, val));
    }

    setTempSearchParams(newParams);

    const label = findFilterLabels(
      trackableFacets,
      facet.search_key,
      choice.value
    );

    pushFilterInteraction({
      action: 'deselect',
      category: label?.category ?? facet.name,
      value: label?.value ?? choice.label,
      activeFilters: getActiveFilterLabels(trackableFacets, newParams)
    });
    router.push(pathname + '?' + newParams.toString(), { scroll: false });
  };

  const selectedChoicesCount = useMemo(() => {
    return selectedFacets.reduce((total, facet) => {
      const choices = facet.data?.choices || [];
      const selectedCount = choices.filter(
        (choice) => choice.is_selected
      ).length;
      return total + selectedCount;
    }, 0);
  }, [selectedFacets]);

  return (
    <>
      <div className="flex flex-col md:px-[8%]">
        <CategoryHeader
          totalCount={data.pagination?.total_count}
          setMenuStatus={() => dispatch(setMenuOpen(!isMenuOpen))}
          selectedChoicesCount={selectedChoicesCount}
          selectedFacets={selectedFacets}
          onRemoveFilter={handleRemoveSelectedFilter}
          sortOptions={data.sorters}
        />
        <div className="flex flex-row py-10 px-2.5 md:px-0">
          {!matchesStatus && (
            <DesktopFilters
              tempSearchParams={tempSearchParams}
              setTempSearchParams={setTempSearchParams}
              categoryPk={data.category?.pk}
              isMenuOpen={isMenuOpen}
              totalCount={data.pagination?.total_count}
              setIsMenuOpen={(open) => dispatch(setMenuOpen(open))}
            />
          )}
          <div
            className={clsx([
              'grid grid-cols-2 gap-x-2.5 gap-y-10',
              'md:grid-cols-3 md:gap-x-4 md:gap-y-4 md:pl-10 md:grow'
            ])}
          >
            {data.products.map((product, index) => {
              const absoluteIndex =
                (data.pagination?.current_page - 1) *
                  data.pagination?.page_size +
                index +
                1;

              return (
                <Fragment key={product.pk}>
                  {renderListingBanners(
                    (productIndex) => productIndex === index
                  )}
                  <ProductItem
                    product={product}
                    width={500}
                    height={500}
                    index={absoluteIndex || index + 1}
                    cardBadges={props.cardBadges}
                    useDefaultDimension={useDefaultDimension}
                    showInspectButton
                  />
                </Fragment>
              );
            })}
            {renderListingBanners(
              (productIndex) => productIndex >= data.products.length
            )}
          </div>
        </div>
        {searchText && (
          <>
            <div
              className="personaclick-full-search-results hidden"
              data-search-query={searchText}
            ></div>

            {data?.products?.length === 0 && emptyRecoSuccess && (
              <PcRecommendationClient data={emptyRecommendation} />
            )}
          </>
        )}
        {data.products.length > 0 && (
          <Pagination
            total={data.pagination.total_count}
            limit={data.pagination.page_size}
            currentPage={data.pagination.current_page}
            numberOfPages={data.pagination.num_pages}
          />
        )}
      </div>

      {recoSuccess && !searchText && (
        <PcRecommendationClient
          data={recommendation}
          className="px-5 lg:px-[5%]"
          progressClassName="px-5 lg:px-[5%]"
        />
      )}

      {matchesStatus && (
        <MobileFilters
          tempSearchParams={tempSearchParams}
          setTempSearchParams={setTempSearchParams}
          categoryPk={data.category?.pk}
          isMenuOpen={isMenuOpen}
          totalCount={data.pagination?.total_count}
          setIsMenuOpen={(open) => dispatch(setMenuOpen(open))}
        />
      )}
    </>
  );
}
